import { createContext, useCallback, useContext, useState } from "react";
import { withSyncPendingRetry } from "../api/client";
import type { BuiltTx } from "../api/types";
import { explorerTx } from "../env";
import { signAndSubmit, type SubmitRoute } from "../wallet/actions";
import { readableChainError } from "../chain-error";
import { useWallet } from "../wallet/WalletProvider";

/** Lets panels outside /play report "waiting for sync" without an overlay. */
export const SyncPendingReporter = createContext<(waiting: boolean) => void>(() => {});

export interface ActionResult {
  signature: string;
  explorer: string;
}

/**
 * Build → wallet sign → submit, with `409 sync_pending` waiting (§15.5.4, WEB-15).
 */
export function useWalletAction() {
  const { wallet } = useWallet();
  const reportGlobal = useContext(SyncPendingReporter);
  const [busy, setBusy] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ActionResult | null>(null);

  const run = useCallback(
    async (build: () => Promise<Pick<BuiltTx, "transaction">>, route: SubmitRoute = "auto"): Promise<ActionResult | null> => {
      if (!wallet) {
        setError("Log in first");
        return null;
      }
      setBusy(true);
      setError(null);
      setResult(null);
      try {
        const built = await withSyncPendingRetry(build, (w) => {
          setWaiting(w);
          reportGlobal(w);
        });
        const signature = await signAndSubmit(wallet, built, route);
        const r = { signature, explorer: explorerTx(signature) };
        setResult(r);
        return r;
      } catch (e) {
        setError(readableChainError(e instanceof Error ? e.message : String(e)));
        return null;
      } finally {
        setBusy(false);
        setWaiting(false);
      }
    },
    [wallet, reportGlobal],
  );

  return { run, busy, waiting, error, result, clearError: () => setError(null) };
}
